import { Grid3X3, RefreshCw, Settings2, X, Activity } from 'lucide-react';
import { useLabOS } from '@/lib/labos';
import { Navigate } from 'react-router-dom';
import { useAdminInstances } from '@/hooks/useAdminInstances';
import { useEffect, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import NodeTracking from '@/components/NodeTracking';

interface ColumnConfig {
  key: string;
  label: string;
  visible: boolean;
  defaultVisible: boolean;
}

const STORAGE_KEY = 'admin-column-preferences';

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: 'user_email', label: 'Created By', visible: true, defaultVisible: true },
  { key: 'user_id', label: 'User ID', visible: false, defaultVisible: false },
  { key: 'tier', label: 'Tier', visible: true, defaultVisible: true },
  { key: 'status', label: 'Status', visible: true, defaultVisible: true },
  { key: 'created_at', label: 'Created At', visible: true, defaultVisible: true },
  // Optional columns
  { key: 'user_tier', label: 'User Tier', visible: false, defaultVisible: false },
  { key: 'build_version', label: 'Build Version', visible: false, defaultVisible: false },
  { key: 'uptime', label: 'Uptime', visible: false, defaultVisible: false },
  { key: 'pod_name', label: 'Pod Name', visible: false, defaultVisible: false },
  { key: 'pod_phase', label: 'Pod Phase', visible: false, defaultVisible: false },
  { key: 'node_name', label: 'Node Name', visible: false, defaultVisible: false },
  { key: 'container_restarts', label: 'Restarts', visible: false, defaultVisible: false },
  { key: 'memory_limit', label: 'Memory Limit', visible: false, defaultVisible: false },
  { key: 'cpu_limit', label: 'CPU Limit', visible: false, defaultVisible: false },
  { key: 'gpu_request', label: 'GPU Request', visible: false, defaultVisible: false },
];

function EmptyTabContent({ title, description }: { title: string; description: string }) {
  return (
    <div className="rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-black/[0.01] dark:bg-white/[0.01] p-12 text-center">
      <p className="text-black/35 dark:text-white/35 text-sm mb-2">{title}</p>
      <p className="text-black/25 dark:text-white/25 text-xs">{description}</p>
    </div>
  );
}

function getStatusColor(status: string): 'default' | 'secondary' | 'destructive' | 'outline' {
  switch (status) {
    case 'ready':
      return 'default';
    case 'starting':
    case 'creating':
      return 'secondary';
    case 'failed':
      return 'destructive';
    default:
      return 'outline';
  }
}

function formatDate(dateString: string): string {
  const date = new Date(dateString);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatUptime(seconds: number | null | undefined): string {
  if (!seconds) return 'N/A';

  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  if (hours > 0) {
    return `${hours}h ${minutes}m`;
  }
  return `${minutes}m`;
}

export default function Admin() {
  const { user } = useLabOS();
  const { instances, loading, error, fetchInstances } = useAdminInstances();
  const [activeTab, setActiveTab] = useState<'instances' | 'node-tracking'>('instances');
  const [showSettings, setShowSettings] = useState(false);
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const savedColumns = JSON.parse(saved);
        return DEFAULT_COLUMNS.map((col) => {
          const savedCol = savedColumns.find((c: ColumnConfig) => c.key === col.key);
          return savedCol ? { ...col, visible: savedCol.visible } : col;
        });
      } catch {
        return DEFAULT_COLUMNS;
      }
    }
    return DEFAULT_COLUMNS;
  });

  // Redirect non-admin users
  if (!user?.isAdmin) {
    return <Navigate to="/" replace />;
  }

  // Fetch instances on mount
  useEffect(() => {
    fetchInstances();
  }, [fetchInstances]);

  const toggleColumn = (key: string) => {
    const updated = columns.map((col) => (col.key === key ? { ...col, visible: !col.visible } : col));
    setColumns(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
  };

  const resetColumns = () => {
    const reset = DEFAULT_COLUMNS.map((col) => ({ ...col, visible: col.defaultVisible }));
    setColumns(reset);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(reset));
  };

  const visibleColumns = columns.filter((col) => col.visible);

  const renderCellValue = (instance: any, columnKey: string) => {
    switch (columnKey) {
      case 'user_email':
        return (
          <div className="flex flex-col">
            {instance.user_email && <span className="text-black/70 dark:text-white/70">{instance.user_email}</span>}
            {!instance.user_email && (
              <span className="font-mono text-black/50 dark:text-white/50 text-xs">{instance.user_id}</span>
            )}
          </div>
        );
      case 'user_id':
        return <span className="font-mono text-black/70 dark:text-white/70">{instance.user_id}</span>;
      case 'tier':
        return (
          <Badge variant="outline" className="font-medium">
            {instance.tier}
          </Badge>
        );
      case 'status':
        return <Badge variant={getStatusColor(instance.status)}>{instance.status}</Badge>;
      case 'created_at':
        return <span className="text-black/60 dark:text-white/60">{formatDate(instance.created_at)}</span>;
      case 'user_tier':
        return instance.user_tier ? <Badge variant="outline">{instance.user_tier}</Badge> : 'N/A';
      case 'build_version':
        return <span className="font-mono text-xs">{instance.build_version || 'unknown'}</span>;
      case 'uptime':
        return formatUptime(instance.uptime_seconds);
      case 'pod_name':
        return <span className="font-mono text-xs">{instance.pod_name || 'N/A'}</span>;
      case 'pod_phase':
        return instance.pod_phase || 'N/A';
      case 'node_name':
        return <span className="font-mono text-xs">{instance.node_name || 'N/A'}</span>;
      case 'container_restarts':
        return (
          <Badge variant={instance.container_restarts > 0 ? 'destructive' : 'outline'}>{instance.container_restarts ?? 0}</Badge>
        );
      case 'memory_limit':
        return instance.memory_limit || 'N/A';
      case 'cpu_limit':
        return instance.cpu_limit || 'N/A';
      case 'gpu_request':
        return instance.gpu_request || 'N/A';
      default:
        return 'N/A';
    }
  };

  return (
    <div className="min-h-full bg-white dark:bg-neutral-950 flex flex-col">
      <main className="w-full px-6 py-12 flex-1">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-semibold text-black/85 dark:text-white/85 tracking-tight">Admin</h1>
            <p className="text-sm text-black/40 dark:text-white/40 mt-1">
              {activeTab === 'instances' ? 'Manage all running instances.' : 'Monitor ComfyUI node usage and analytics.'}
            </p>
          </div>
          <div className="flex gap-2">
            {activeTab === 'instances' && (
              <Button
                onClick={() => setShowSettings(!showSettings)}
                variant="outline"
                size="sm"
                className="flex items-center gap-2"
              >
                <Settings2 className="w-4 h-4" />
                Columns
              </Button>
            )}
            {activeTab === 'instances' && (
              <Button onClick={fetchInstances} disabled={loading} variant="outline" size="sm" className="flex items-center gap-2">
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Refresh Instances
              </Button>
            )}
          </div>
        </div>

        {/* Column Settings Panel */}
        {showSettings && (
          <div className="mb-6 rounded-xl border border-black/[0.06] dark:border-white/[0.06] bg-black/[0.01] dark:bg-white/[0.01] p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-medium text-black/70 dark:text-white/70">Column Visibility</h3>
              <div className="flex gap-2">
                <Button onClick={resetColumns} variant="outline" size="sm" className="text-xs">
                  Reset to Default
                </Button>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-black/40 dark:text-white/40 hover:text-black/70 dark:hover:text-white/70"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {columns.map((col) => (
                <label key={col.key} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={col.visible}
                    onChange={() => toggleColumn(col.key)}
                    className="rounded border-black/20 dark:border-white/20 text-black dark:text-white focus:ring-black/20 dark:focus:ring-white/20"
                  />
                  <span className="text-sm text-black/60 dark:text-white/60">{col.label}</span>
                </label>
              ))}
            </div>
          </div>
        )}

        <div className="flex gap-1 mb-8 border-b border-black/[0.06] dark:border-white/[0.06]">
          <button
            onClick={() => {
              setActiveTab('instances');
              setShowSettings(false);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
              activeTab === 'instances'
                ? 'border-black/80 dark:border-white/80 text-black/85 dark:text-white/85'
                : 'border-transparent text-black/50 dark:text-white/50 hover:text-black/70 dark:hover:text-white/70'
            }`}
          >
            <Grid3X3 className="w-4 h-4" />
            Instances ({instances.length})
          </button>
          <button
            onClick={() => {
              setActiveTab('node-tracking');
              setShowSettings(false);
            }}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all border-b-2 -mb-px ${
              activeTab === 'node-tracking'
                ? 'border-black/80 dark:border-white/80 text-black/85 dark:text-white/85'
                : 'border-transparent text-black/50 dark:text-white/50 hover:text-black/70 dark:hover:text-white/70'
            }`}
          >
            <Activity className="w-4 h-4" />
            Node Tracking
          </button>
        </div>

        {activeTab === 'instances' && (
          <>
            {loading && instances.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <Spinner />
              </div>
            ) : error ? (
              <div className="rounded-xl border border-red-200 bg-red-50 p-12 text-center">
                <p className="text-red-600 text-sm mb-2">Error loading instances</p>
                <p className="text-red-500 text-xs">{error}</p>
              </div>
            ) : instances.length === 0 ? (
              <EmptyTabContent title="No running instances" description="There are currently no active instances" />
            ) : (
              <div className="rounded-xl border border-black/[0.06] dark:border-white/[0.06] overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-black/[0.02] dark:bg-white/[0.02] border-b border-black/[0.06] dark:border-white/[0.06]">
                    <tr>
                      {visibleColumns.map((col) => (
                        <th
                          key={col.key}
                          className="text-left px-6 py-3 text-xs font-medium text-black/50 dark:text-white/50 uppercase tracking-wider"
                        >
                          {col.label}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white dark:bg-neutral-800 divide-y divide-black/[0.06] dark:divide-white/[0.06]">
                    {instances.map((instance, index) => (
                      <tr
                        key={`${instance.user_id}-${instance.tier}-${index}`}
                        className="hover:bg-black/[0.01] dark:hover:bg-white/[0.02]"
                      >
                        {visibleColumns.map((col) => (
                          <td key={col.key} className="px-6 py-4 text-sm whitespace-nowrap">
                            {renderCellValue(instance, col.key)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'node-tracking' && <NodeTracking />}
      </main>
      <footer className="px-6 pb-6">
        <p className="text-center text-xs text-slate-500 dark:text-neutral-500">
          © 2026 Warner Bros. Discovery. Internal use only.
        </p>
      </footer>
    </div>
  );
}
